// @greenlight-spec: 59-platform-agent.md#entry-points-elsewhere
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Telescope } from 'lucide-react';
import { toast } from 'sonner';

import { relativeTime } from '@/components/greenlight/relativeTime';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import {
  GATHER_KNOWLEDGE_SESSION,
  LAST_TEST_BY_SLUG,
  sessionHref,
} from './integrationAgentFixtures';
import { LiveStepLine, useCycle, type TestVerdict } from './Transcript';

const VERDICT: Record<TestVerdict, { label: string; dot: string }> = {
  passed: { label: 'passed', dot: 'bg-gl-green' },
  passed_with_findings: { label: 'passed with findings', dot: 'bg-gl-amber' },
  failed: { label: 'failed', dot: 'bg-gl-red' },
};

/**
 * The integration card's Greenlight Agents entry points: Gather knowledge, and
 * the latest Integration tester verdict. Rendered inside the card's action row.
 */
export function IntegrationAgentActions({
  slug,
  name,
}: {
  slug: string;
  name: string;
}): React.JSX.Element {
  const navigate = useNavigate();
  const [exploring, setExploring] = React.useState(false);
  const step = useCycle(
    [
      `Reading ${name}’s developer docs…`,
      'Listing objects this credential can see…',
      'Sampling 3 rows…',
      'Checking existing Knowledge…',
    ],
    2600,
  );
  const last = LAST_TEST_BY_SLUG[slug];
  const href = sessionHref(GATHER_KNOWLEDGE_SESSION, name);

  const gather = () => {
    setExploring(true);
    toast.success('Knowledge explorer started', {
      description: `Exploring ${name}. Proposals land in its Knowledge for review.`,
      action: { label: 'View session', onClick: () => navigate(href) },
    });
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="min-h-11 md:min-h-0"
        onClick={gather}
        disabled={exploring}
        data-testid={`gather-knowledge-${slug}`}
      >
        <Telescope aria-hidden /> {exploring ? 'Gathering knowledge…' : 'Gather knowledge'}
      </Button>
      <span className="order-last ml-auto flex min-w-0 items-center text-[12px]">
        {exploring ? (
          <Link to={href} className="flex min-w-0 items-center gap-2 hover:underline">
            <LiveStepLine text={step ?? ''} />
            <span className="shrink-0 text-link">View session</span>
          </Link>
        ) : last ? (
          <Link
            to={`/agents/integration-tester/sessions/${last.sessionId}?target=${encodeURIComponent(name)}`}
            className="flex items-center gap-1.5 hover:underline"
            data-testid={`last-agent-test-${slug}`}
          >
            <span className={cn('size-2 rounded-full', VERDICT[last.verdict].dot)} aria-hidden />
            Last test: {VERDICT[last.verdict].label} · {relativeTime(last.at)}
          </Link>
        ) : (
          <span className="text-muted-foreground">Not tested by an agent yet</span>
        )}
      </span>
    </>
  );
}
